import React, { useState, useMemo } from "react";
import {
  ChakraProvider,
  Box,
  Input,
  Image,
  Link,
  Heading,
  Text,
  HStack,
  Tag,
  useColorModeValue,
  Divider,
  Button,
  VisuallyHidden,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import { createColumnHelper } from "@tanstack/react-table";
import { DataTable } from "./data/DataTable";
import fireccilt11 from "./data/img/firecci_fig2.png";
import viirsv002 from "./data/img/viirsv002.png";
import mzNASAGrant from "./data/img/mz_nasa_grant.png";

// BlogTags Component with accessible markup
const BlogTags = ({ tags }) => (
  <HStack spacing={2} marginTop={2} as="ul" listStyleType="none" aria-label="Tags">
    {tags.map((tag) => (
      <Tag
        as="li"
        size={"md"}
        variant="solid"
        colorScheme="orange"
        key={tag}
      >
        {tag}
      </Tag>
    ))}
  </HStack>
);

const BlogAuthor = ({ name, date }) => (
  <HStack marginTop="2" spacing="2" display="flex" alignItems="center">
    <Text fontWeight="medium">{name}</Text>
    <Text aria-hidden="true">—</Text>
    <Text>{date}</Text>
  </HStack>
);

// News Data - Add `pinned: true` to pin an item at the top (only when not filtering)
const data = [
  {
    image: null,
    tags: ["Announcement"],
    title: "Associate Research Professor Joanne Hall Leads Global Field Campaigns to Track Crop Residue Burning",
    description: "Associate Research Professor Joanne Hall organized three crop residue burning field campaigns in summer and fall 2025 as part of her NASA Carbon Monitoring System (CMS) project aimed at improving crop residue burning and emission estimates for the EPA's National Emissions Inventory (NEI).",
    author: "J. Hall",
    date: "2025-12-11",
    link: "https://geog.umd.edu/featured-content/associate-research-professor-joanne-hall-leads-global-field-campaigns-track-crop",
    pinned: false,
  },
  {
    image: null,
    tags: ["Announcement"],
    title: "NASA Honors Research Professor Louis Giglio With Prestigious Public Leadership Medal",
    description: "NASA has recently announced this year's recipients of its Honor Awards, recognizing exceptional contributions across the agency. Among them is Louis Giglio, a research professor at the Department of Geographical Sciences, who has received the Outstanding Public Leadership Medal. This medal honors nongovernment individuals for sustained, high-impact leadership that significantly advances NASA's mission and image. Recipients are recognized for delivering meaningful results, modeling NASA's core values, fostering collaboration and influencing lasting change.",
    author: "L. Giglio",
    date: "2025-06-09",
    link: "https://geog.umd.edu/featured-content/nasa-honors-research-professor-louis-giglio-prestigious-public-leadership-medal",
    pinned: false, // Example: Set to true to pin this item
  },
  {
    image: null,
    tags: ["Announcement"],
    title: "BSOS Professors — 'They're Just Like Us!'",
    description: "Ellicott is a firefighter type 2 with the Maryland Department of Natural Resources\' Forest Service Wildland Fire Crew. Through a Cooperative Agreement with the USDA Forest Service, he and his fellow crew members volunteer to deploy to different parts of the country to fight wildfires from late June-early September, though he says, \"I\'ve learned a bit about structure fires since the interface is often crossed.\" " ,
    author: "E. Ellicott",
    date: "2025-05-28",
    link: "https://geog.umd.edu/featured-content/bsos-professors-theyre-just-us",
    pinned: false,
  },
  {
    image: null,
    tags: ["Announcement"],
    title: "UMD Team Featuring GEOG Scientist Among Semifinalists in Global XPRIZE Wildfire Challenge",
    description: "Experts from several disciplines are working together to use an advanced drone-based system to detect and extinguish wildfires within minutes.",
    author: "E. Ellicott",
    date: "2024-11-07",
    link: "https://geog.umd.edu/featured-content/umd-team-featuring-geog-scientist-among-semifinalists-global-xprize-wildfire",
    pinned: false,
  },
  {
    image: null,
    tags: ["Announcement"],
    title: "NASA Carbon Monitoring System Awards Major Grants to GEOG Faculty",
    description: "NASA's Carbon Monitoring System (CMS) program has announced the selection of two research projects and the renewal of the CMS Science Team Leader from the Department of Geographical Sciences, awarding over $2 million in total funding to advance carbon science.",
    date: "2024-11-11",
    author: "J. Hall",
    link: "https://geog.umd.edu/featured-content/nasa-carbon-monitoring-system-awards-major-grants-geog-faculty",
    pinned: false,
  },
  {
    image: null,
    tags: ["Announcement"],
    title: "NASA's FireSense Team Selects GEOG Researchers to Tackle Wildfire Challenges",
    description:
      "Three GEOG faculty members—Professor and Chair Tatiana Loboda, Associate Research Professor Evan Ellicott and Assistant Research Professor Adrián Pascual Arranz—have been selected to join NASA's FireSense Implementation (FSI) Team. This initiative aims to enhance wildland fire management across the United States using advanced Earth science technologies.",
    author: "T. Loboda, E. Ellicott, A. Pascual Arranz",
    date: "2024-11-11",
    link: "https://geog.umd.edu/featured-content/nasas-firesense-team-selects-geog-researchers-tackle-wildfire-challenges",
    pinned: false,
  },
  {
    image: viirsv002,
    tags: ["VIIRS", "Version 2 Release", "Burned Area"],
    title: "LP DAAC Releases VIIRS Version 2 Burned Area Data Product",
    description:
      "The LP DAAC is pleased to announce the availability of the Visible Infrared Imaging Radiometer Suite (VIIRS) Version 2 Burned Area data product. The VIIRS Version 2 release includes a data product from the Suomi National Polar-orbiting Partnership (Suomi NPP) satellite. This release covers the mission period from March 2012 up to the leading edge. Details on product improvements for Version 2 are provided on the Digital Object Identifier (DOI) landing page.",
    author: "L. Giglio",
    date: "2024-10-23",
    link: "https://lpdaac.usgs.gov/products/vnp64a1v002/",
    pinned: false,
  },
  {
    image: mzNASAGrant,
    tags: ["NASA Grant", "Prescribed", "Fire Mapping", "Everglades"],
    title: "NASA Grant Awarded to Develop Prescribed Fire Mapping in the Everglades",
    description:
      "Assistant Research Professor Maria Zubkova has received NASA funding to develop a decision support system that uses satellite data to automate the mapping of prescribed fires in Florida's Everglades Headwaters...",
    author: "M. Zubkova",
    date: "2024-11-11",
    link: "https://geog.umd.edu/featured-content/nasa-grant-support-prescribed-fire-mapping-everglades",
    pinned: false,
  },
  {
    image: fireccilt11,
    tags: ["Open Letter"],
    title: "ESA Burned area product artifacts may confound the link between biomass burning and infant mortality",
    description:
      "Open letter to Pullabhotla et al. use of the FireCCILT11 biomass burning exposure data due to known flaws in the product...",
    author: "L. Giglio, D. Roy",
    date: "2023-12-07",
    link: "https://doi.org/10.1073/pnas.2317759120",
    pinned: false,
  },
];

// Column Helper
const columnHelper = createColumnHelper();

// Format date for display with accessible format
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
  });
};

// Columns Configuration with accessibility improvements
const columns = [
  columnHelper.accessor("date", {
    cell: (info) => {
      const dateValue = info.getValue();
      const formattedDate = formatDate(dateValue);
      return (
        <time dateTime={dateValue} aria-label={`Published ${formattedDate}`}>
          {formattedDate}
        </time>
      );
    },
    header: "Date",
    sortDescFirst: true,
  }),
  columnHelper.accessor("author", {
    cell: (info) => info.getValue(),
    header: "Personnel",
  }),
  columnHelper.accessor((row) => row.title, {
    id: "details",
    cell: (info) => {
      const row = info.row.original;
      return (
        <Box
          display="flex"
          flex="1"
          flexDirection="column"
          justifyContent="center"
          marginTop={{ base: "3", sm: "0" }}
        >
          <BlogTags tags={row.tags} />
          <Heading as="h3" size="md" marginTop="1">
            <Link
              href={row.link}
              isExternal
              color="blue.700"
              _hover={{ textDecoration: "underline", color: "blue.900" }}
              _focus={{ outline: "2px solid", outlineColor: "blue.500", outlineOffset: "2px" }}
              aria-label={`Read more about: ${row.title} (opens in new tab)`}
            >
              {row.title}
              <VisuallyHidden> (opens in new tab)</VisuallyHidden>
            </Link>
          </Heading>
          <Text as="p" marginTop="2" color="gray.700" fontSize="md">
            {row.description}
          </Text>
        </Box>
      );
    },
    header: "Details",
  }),
  columnHelper.accessor("link", {
    id: "actions",
    cell: (info) => {
      const row = info.row.original;
      return (
        <Button
          as={Link}
          href={info.getValue()}
          isExternal
          size="sm"
          colorScheme="blue"
          variant="outline"
          rightIcon={<ExternalLinkIcon aria-hidden="true" />}
          aria-label={`View article: ${row.title} (opens in new tab)`}
          _hover={{ textDecoration: "none", bg: "blue.50" }}
          _focus={{ outline: "2px solid", outlineColor: "blue.500", outlineOffset: "2px" }}
        >
          View Article
        </Button>
      );
    },
    header: "Link",
    enableSorting: false,
  }),
];

export default function ArchiveNews() {
  const [searchQuery, setSearchQuery] = useState("");

  // Process data: sort by date descending, handle pinning
  const processedData = useMemo(() => {
    const isFiltering = searchQuery.trim().length > 0;

    // Filter data based on search query
    let result = data.filter((row) =>
      Object.values(row)
        .join(" ")
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    );

    // Sort by date descending (most recent first)
    result = result.sort((a, b) => new Date(b.date) - new Date(a.date));

    // If not filtering, apply pinning (pinned items at top)
    if (!isFiltering) {
      const pinnedItems = result.filter((item) => item.pinned === true);
      const unpinnedItems = result.filter((item) => item.pinned !== true);
      result = [...pinnedItems, ...unpinnedItems];
    }

    return result;
  }, [searchQuery]);

  const resultCount = processedData.length;

  return (
    <ChakraProvider>
      <Box p={6} as="section" aria-labelledby="archive-news-heading">
        <VisuallyHidden>
          <Heading as="h2" id="archive-news-heading">
            Archive News
          </Heading>
        </VisuallyHidden>

        <Box as="form" role="search" mb={4}>
          <label htmlFor="news-search">
            <VisuallyHidden>Search news articles</VisuallyHidden>
          </label>
          <Input
            id="news-search"
            placeholder="Search by title, authors, tags, or date..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            aria-describedby="search-results-status"
            _focus={{
              borderColor: "blue.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-blue-500)",
            }}
          />
        </Box>

        {/* Live region for announcing search results to screen readers */}
        <Box
          id="search-results-status"
          aria-live="polite"
          aria-atomic="true"
        >
          <VisuallyHidden>
            {searchQuery
              ? `${resultCount} result${resultCount !== 1 ? "s" : ""} found for "${searchQuery}"`
              : `Showing all ${resultCount} news articles`}
          </VisuallyHidden>
        </Box>

        <DataTable
          columns={columns}
          data={processedData}
          aria-label="Archive news articles"
        />
      </Box>
    </ChakraProvider>
  );
}